import type PhotoSwipeLightbox from 'photoswipe/lightbox'
import type Slider from '~/components/Slider/index.vue'
import type { MediaImageFragment } from '#build/graphql-operations'
import { setGallery } from 'vue-preview-imgs'
import { falsy } from '~/helpers/type'
import IconArrowLeft from '~/assets/symbols/chevron-left.svg?raw'
import IconArrowRight from '~/assets/symbols/chevron-right.svg?raw'
import IconCross from '~/assets/symbols/cross.svg?raw'

type UseLightboxOptions = {
  /**
   * The ref containing the mounted Slider component.
   *
   * If provided, the current active slide is synced with the current slide in
   * the lightbox.
   */
  slider?: Ref<InstanceType<typeof Slider> | null>

  /**
   * Show the caption.
   */
  showCaption?: boolean
}

/**
 * Provides the lightbox feature from PhotoSwipe.
 */
export default function (
  /**
   * The ID of the element containing the thumbnail images.
   */
  id: string,

  /**
   * An array of media image objects from GraphQL.
   */
  mediaImages: MediaImageFragment[],

  options?: UseLightboxOptions,
) {
  const route = useRoute()
  const router = useRouter()
  const config = useRuntimeConfig()

  /**
   * Open the lightbox for the given index.
   */
  const openLightbox = (index: number) => {
    router.push({ query: { ...route.query, gallery: id, index } })
  }

  /**
   * Get the attributes for the slide item. This is needed for the transition
   * to work.
   */
  const getItemAttributes = (index: number) => ({
    'data-id': index,
  })

  // Early return on server because the following code only works client side.
  if (import.meta.server) {
    return {
      openLightbox,
      getItemAttributes,
    }
  }

  const lightboxOpen = computed(() => route.query.gallery === id)

  const lightboxImages = computed(() =>
    mediaImages
      .map((v, id) => {
        const hash = v.image?.file?.rokka?.hash
        if (hash) {
          const maxWidth = 5000
          const maxHeight = 5000

          // Should the source width and height not be available assume a square image.
          const sourceWidth = v.image?.file?.rokka?.sourceWidth || 5000
          const sourceHeight = v.image?.file?.rokka?.sourceHeight || 5000

          // Calculate aspect ratio of original image.
          const aspectRatio = sourceWidth / sourceHeight

          let w = sourceWidth
          let h = sourceHeight

          // Scale down if the source dimensions exceed the maximum dimensions.
          if (w > maxWidth || h > maxHeight) {
            if (aspectRatio > 1) {
              // Landscape or square.
              w = maxWidth
              h = Math.round(maxWidth / aspectRatio)
            } else {
              // Portrait.
              h = maxHeight
              w = Math.round(maxHeight * aspectRatio)
            }
          }

          // Make sure we don't scale up the image.
          if (sourceWidth <= maxWidth && sourceHeight <= maxHeight) {
            w = sourceWidth
            h = sourceHeight
          }

          let src = buildRokkaUrl(
            'fe_nuxt_fullscreen_preview',
            '',
            hash,
            (v.name || 'image.jpg').replace('.png', '.jpg'),
            config.public.rokkaHost,
            {
              w: maxWidth,
              h: maxHeight,
            },
          )

          if (!src.includes('.jpg')) {
            src += '.jpg'
          }

          return {
            src,
            id,
            alt: v.image?.alt,
            caption: v.caption,
            w,
            h,
          }
        }
      })
      .filter(falsy),
  )

  /**
   * Get the initial open index.
   */
  const getInitialIndex = (): number => {
    const indexValue = route.query.index
    if (indexValue && typeof indexValue === 'string') {
      const index = parseInt(indexValue)
      if (
        !Number.isNaN(index) &&
        index >= 0 &&
        index < lightboxImages.value.length
      ) {
        return index
      }
    }

    return 0
  }

  const lightbox: PhotoSwipeLightbox = setGallery({
    arrowPrevSVG: `<div>${IconArrowLeft}</div>`,
    arrowNextSVG: `<div>${IconArrowRight}</div>`,
    dataSource: lightboxImages.value,
    showHideAnimationType: 'zoom',
    bgOpacity: 1,
    counter: false,
    zoom: false,
    closeSVG: IconCross,
  })

  lightbox.addFilter('thumbEl', (thumbEl, data) => {
    const galleryEl = document.getElementById(id)
    if (galleryEl) {
      const el = galleryEl.querySelector(`[data-id="${data.id}"] img`)
      if (el instanceof HTMLElement) {
        return el
      }
    }
    return thumbEl || document.body
  })

  lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
    const galleryEl = document.getElementById(id)
    if (galleryEl) {
      const el = galleryEl.querySelector(`[data-id="${slide.data.id}"] img`)
      if (el instanceof HTMLImageElement) {
        return el.currentSrc
      }
    }
    return placeholderSrc
  })

  /**
   * Update the current slide in the slider when the lightbox slide changes.
   */
  lightbox.on('change', () => {
    const index = lightbox.pswp?.currIndex || 0
    if (options?.slider?.value) {
      const flicking = options.slider.value.getFlicking()
      if (flicking) {
        // The visible ratio is a value between 0 and 1. 0 means the slide is
        // currently not visible. In this case, we update the current slide so
        // that it becomes visible.
        const visibleRatio = flicking.getPanel(index)?.visibleRatio
        if (!visibleRatio) {
          flicking.moveTo(index, 0)
        }
      }
    }
    router.replace({ query: { ...route.query, index } })
  })

  /**
   * Remove the gallery query params from the current route.
   */
  lightbox.on('close', function () {
    router.push({
      query: { ...route.query, gallery: undefined, index: undefined },
    })
  })

  /**
   * Adds the pagination indicator bullets.
   */
  lightbox.on('uiRegister', function () {
    if (!lightbox.pswp?.ui) {
      return
    }
    lightbox.pswp.ui.registerElement({
      name: 'bulletsIndicator',
      className: 'pswp__bullets-indicator',
      appendTo: 'wrapper',
      onInit: (el, pswp) => {
        const bullets: HTMLDivElement[] = []
        let bullet
        let prevIndex = -1

        for (let i = 0; i < pswp.getNumItems(); i++) {
          bullet = document.createElement('div')
          bullet.className = 'pswp__bullet'
          bullet.onclick = (e) => {
            if (e.target instanceof HTMLElement) {
              pswp.goTo(i)
            }
          }
          el.appendChild(bullet)
          bullets.push(bullet)
        }

        pswp.on('change', () => {
          if (prevIndex >= 0) {
            bullets[prevIndex].classList.remove('pswp__bullet--active')
          }
          bullets[pswp.currIndex].classList.add('pswp__bullet--active')
          prevIndex = pswp.currIndex
        })
      },
    })

    if (options?.showCaption) {
      lightbox.pswp.ui.registerElement({
        name: 'custom-caption',
        order: 9,
        isButton: false,
        appendTo: 'wrapper',
        html: 'Caption',
        onInit: (el) => {
          if (!lightbox.pswp) {
            return
          }
          lightbox.pswp.on('change', () => {
            const alt = lightbox.pswp?.currSlide?.data?.caption
            el.innerHTML = alt || ''
          })
        },
      })
    }
  })

  watch(lightboxOpen, (isOpen) =>
    isOpen ? lightbox.loadAndOpen(getInitialIndex()) : lightbox.pswp?.close(),
  )

  onMounted(() => {
    if (lightboxOpen.value) {
      lightbox.loadAndOpen(getInitialIndex())
    }
  })

  onBeforeUnmount(() => {
    if (lightbox) {
      lightbox.destroy()
    }
  })

  return {
    openLightbox,
    getItemAttributes,
  }
}
